import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getUpcomingClasses } from '../../services/classService';
import { prepareLiveLessonsSchedule } from '../../utils/liveLessonsSchedule';
import { DaysOfTheWeekTrans, LandingTrans, ScheduleTrans } from '../../utils/translations';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import SectionLabel from '../ui/SectionLabel';
import SectionTitle from '../ui/SectionTitle';

function formatTimeAndDuration(slot, minLabel) {
  const parts = [];
  if (slot.time) parts.push(slot.time);
  if (slot.duration != null && slot.duration !== '') parts.push(`${slot.duration} ${minLabel}`);
  return parts.join(' · ');
}

function ScheduleColumn({ eyebrow, slots, scheduleT, showLocationLinks, suppressSlotTitles }) {
  const hideTitle = (title) =>
    title && suppressSlotTitles?.length && suppressSlotTitles.includes(title);
  return (
    <div className="min-w-0">
      <p className="text-[13px] font-sans font-medium uppercase tracking-[0.18em] text-clay mb-6 pb-3 border-b border-sand-dark">
        {eyebrow}
      </p>
      {slots.length === 0 ? (
        <p className="text-sm font-sans font-light text-ink-mid italic leading-relaxed">
          {scheduleT.comingSoon}
        </p>
      ) : (
        <ul className="space-y-5 list-none m-0 p-0">
          {slots.map((slot) => (
            <li
              key={slot.key}
              className="bg-warm-white rounded-sm border border-sand-dark p-6 md:p-7 shadow-sm text-center"
            >
              {!hideTitle(slot.title) ? (
                <h3 className="font-serif text-center text-xl md:text-2xl font-medium text-ink leading-snug mb-2">
                  {slot.title}
                </h3>
              ) : null}
              {slot.daysLine ? (
                <p
                  className={`text-sm font-sans font-light text-ink-mid leading-relaxed ${
                    hideTitle(slot.title) ? 'mb-2' : 'mb-3'
                  }`}
                >
                  {slot.daysLine}
                </p>
              ) : null}
              {formatTimeAndDuration(slot, scheduleT.min) ? (
                <p className="text-sm font-sans font-medium text-ink tracking-wide">
                  {formatTimeAndDuration(slot, scheduleT.min)}
                </p>
              ) : null}
              {showLocationLinks && slot.location?.url ? (
                <a
                  href={slot.location.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-xs font-sans uppercase tracking-widest text-clay border-b border-clay pb-0.5 hover:opacity-80"
                >
                  {slot.location.label}
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LandingScheduleSection({
  showLocationLinks = true,
  sectionId = 'schedule',
  suppressSlotTitles,
  customScheduleHeading,
} = {}) {
  const { language, token } = useAppContext();
  const t = LandingTrans[language];
  const scheduleT = ScheduleTrans[language];
  const daysTrans = DaysOfTheWeekTrans[language];
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, visible] = useScrollReveal();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getUpcomingClasses(token);
        if (!cancelled) setClasses(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const { morning, evening } = useMemo(
    () => prepareLiveLessonsSchedule(classes, scheduleT, daysTrans),
    [classes, scheduleT, daysTrans]
  );

  return (
    <section
      ref={ref}
      id={sectionId}
      className={`bg-cream py-16 md:py-30 px-6 md:px-20 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {!(customScheduleHeading?.omitLabel) ? (
          <SectionLabel>{t.scheduleSectionLabel}</SectionLabel>
        ) : null}
        <SectionTitle
          className={
            customScheduleHeading?.omitLabel
              ? 'mb-10 md:mb-12'
              : 'mt-4 mb-10 md:mb-12'
          }
        >
          {customScheduleHeading ? (
            <>
              {customScheduleHeading.title}
              {customScheduleHeading.titleEm ? (
                <em className="italic text-clay">{customScheduleHeading.titleEm}</em>
              ) : null}
            </>
          ) : (
            <>
              {t.scheduleSectionTitle}
              <em className="italic text-clay">{t.scheduleSectionTitleEm}</em>
            </>
          )}
        </SectionTitle>

        {loading ? (
          <p className="text-sm font-sans text-ink-mid">{scheduleT.loading}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 lg:gap-20">
            <ScheduleColumn
              eyebrow={scheduleT.morningPracticeHeader}
              slots={morning}
              scheduleT={scheduleT}
              showLocationLinks={showLocationLinks}
              suppressSlotTitles={suppressSlotTitles}
            />
            <ScheduleColumn
              eyebrow={scheduleT.eveningPracticeHeader}
              slots={evening}
              scheduleT={scheduleT}
              showLocationLinks={showLocationLinks}
              suppressSlotTitles={suppressSlotTitles}
            />
          </div>
        )}
      </div>
    </section>
  );
}
